<template>
  <div v-if="filteredComics.length" class="category-shelf mb-8">
    <div class="d-flex align-center justify-space-between mb-3 px-1">
      <div class="d-flex align-center ga-2">
        <h3 class="text-h6 font-weight-bold">{{ category }}</h3>
        <v-chip size="x-small" color="comics" variant="tonal" class="font-weight-bold">
          {{ filteredComics.length }}
        </v-chip>
      </div>

      <!-- Scroll Nav Controls -->
      <div class="d-flex align-center ga-1">
        <v-btn
          icon
          size="x-small"
          variant="tonal"
          class="shelf-nav-btn"
          aria-label="Scroll left"
          @click="scrollLeft"
        >
          <v-icon size="18">mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn
          icon
          size="x-small"
          variant="tonal"
          class="shelf-nav-btn"
          aria-label="Scroll right"
          @click="scrollRight"
        >
          <v-icon size="18">mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Shelf Track -->
    <div ref="trackRef" class="shelf-carousel-track">
      <div
        v-for="comic in filteredComics"
        :key="itemKey(comic)"
        class="shelf-item"
      >
        <router-link
          :to="posterLink(comic)"
          class="comic-poster-card poster-card poster-card--comics d-block"
        >
          <div class="poster-image-container">
            <v-img
              :src="posterSrc(comic)"
              :aspect-ratio="2 / 3"
              cover
              class="rounded-lg w-100"
            >
              <template #placeholder>
                <div class="d-flex flex-column align-center justify-center fill-height fallback-poster">
                  <v-icon size="36" color="#f59e0b">mdi-draw</v-icon>
                  <span class="text-caption text-center px-2 mt-1 text-truncate w-100 font-weight-medium">
                    {{ comic.title }}
                  </span>
                </div>
              </template>
            </v-img>

            <div class="poster-badges-top">
              <div v-if="comic.rating && Number(comic.rating) > 0" class="rating-badge">
                <v-icon size="12" color="amber" class="mr-0.5">mdi-star</v-icon>
                <span>{{ comic.rating }}</span>
              </div>
              <div v-if="comic.issue_number" class="issue-badge">
                #{{ comic.issue_number }}
              </div>
            </div>

            <div class="poster-overlay-bottom">
              <div class="poster-title text-truncate font-weight-bold">{{ comic.title }}</div>
              <div class="poster-subtitle d-flex justify-space-between align-center">
                <span v-if="comic.publisher" class="text-truncate">{{ comic.publisher }}</span>
                <span v-else-if="comic.author" class="text-truncate">{{ comic.author }}</span>
                <span v-else-if="comic.year" class="ml-auto">{{ comic.year }}</span>
              </div>
            </div>
          </div>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { posterSrc, comicPosterLink } from '@/utils/comicForm'

export default {
  name: 'ComicRowCategory',
  props: {
    category: { type: String, required: true },
    comics: { type: Array, default: () => [] },
    displayMode: { type: String, default: 'all' },
  },
  setup() {
    const trackRef = ref(null)

    const scrollLeft = () => {
      if (trackRef.value) {
        trackRef.value.scrollBy({ left: -360, behavior: 'smooth' })
      }
    }

    const scrollRight = () => {
      if (trackRef.value) {
        trackRef.value.scrollBy({ left: 360, behavior: 'smooth' })
      }
    }

    return { trackRef, scrollLeft, scrollRight }
  },
  computed: {
    filteredComics() {
      return this.comics
        .filter((comic) => comic.category === this.category)
        .slice()
        .sort((a, b) => new Date(b.created) - new Date(a.created))
    },
  },
  methods: {
    posterSrc,
    posterLink(comic) {
      return comicPosterLink(comic, this.displayMode)
    },
    itemKey(comic) {
      if (this.displayMode === 'series' && comic.serie_id != null) {
        return `serie-${comic.serie_id}`
      }
      return comic.id
    },
  },
}
</script>

<style scoped>
.category-shelf {
  position: relative;
}

.shelf-nav-btn {
  border-radius: 50% !important;
}

.shelf-carousel-track {
  display: flex;
  gap: 14px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 4px 4px 14px 4px;
  scrollbar-width: thin;
}

.shelf-item {
  flex: 0 0 155px;
  max-width: 155px;
}

@media (max-width: 600px) {
  .shelf-item {
    flex: 0 0 120px;
    max-width: 120px;
  }
}

.comic-poster-card {
  text-decoration: none;
  color: inherit;
}

.poster-image-container {
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  background: rgba(128, 128, 128, 0.08);
}

.fallback-poster {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(251, 191, 36, 0.1));
}

.poster-badges-top {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
  z-index: 2;
}

.rating-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 6px;
}

.issue-badge {
  background: rgba(245, 158, 11, 0.9);
  backdrop-filter: blur(8px);
  color: #000;
  font-size: 0.65rem;
  font-weight: 800;
  padding: 2px 5px;
  border-radius: 4px;
  margin-left: auto;
}

.poster-overlay-bottom {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 24px 8px 8px 8px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.6) 60%, transparent 100%);
  color: #fff;
  z-index: 2;
}

.poster-title {
  font-size: 0.8rem;
  line-height: 1.2;
}

.poster-subtitle {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}
</style>

